import { useEffect, useMemo, useState } from 'react';
import Modal from '@/common/Modal';
import { TourBagService } from '@/service';
import { DeletedTourBag } from '@/api/models';
import CloseXIcon from '@/assets/icons/close-x.svg?react';
import styles from './DeletedHistory.module.scss';
import { getColumnsForHeadType } from './deletedHistoryColumns';
import { useToast } from '@/context/ToastContext';

interface IDeletedHistoryProps {
  headTypeId: number;
  playerId: number;
  playerName: string;
  showDeletedHistory: boolean;
  closeDeletedHistory: () => void;
}

export default function DeletedHistory({ playerId, playerName, showDeletedHistory, closeDeletedHistory, headTypeId }: IDeletedHistoryProps) {
  const [deletedHistory, setDeletedHistory] = useState<DeletedTourBag[]>([]);
  const [loading, setLoading] = useState(true);
  const columns = useMemo(() => getColumnsForHeadType(headTypeId), [headTypeId]);
  const { showToast } = useToast();

  useEffect(() => {
    const getDeleteHistory = async () => {
      try {
        const deletedClubs = await TourBagService.getDeletedTourBagHistory(playerId || 0, headTypeId);
        setDeletedHistory(deletedClubs);
      } catch {
        closeDeletedHistory();
        showToast({ message: 'Failed to retrieve deleted club history', status: 'error' });
      } finally {
        setLoading(false);
      }
    };

    if (showDeletedHistory) {
      getDeleteHistory();
    }
  }, [headTypeId, playerId, showDeletedHistory]);

  return (
    <Modal isOpen={showDeletedHistory} onClose={closeDeletedHistory} className={styles.modal}>
      <div className={styles.navbar}>
        <span className={styles.title}>Deleted Clubs - {playerName}</span>
        <span>
          <CloseXIcon className={styles.closeX} onClick={closeDeletedHistory} />
        </span>
      </div>

      <div className={styles.horizontalScrollContainer}>
        {loading && <div className={styles.loading}>Loading...</div>}

        {!loading && deletedHistory.length === 0 && <div className={styles.loading}>No deleted clubs found.</div>}

        {!loading && deletedHistory.length > 0 && (
          <table className={styles.table}>
            <thead>
              <tr>
                {columns.map((col) => (
                  <th key={col.header}>{col.header}</th>
                ))}
              </tr>
            </thead>

            <tbody>
              {deletedHistory.map((club, ix) => (
                <tr key={ix}>
                  {columns.map((col) => (
                    <td key={col.header} className={col.header === 'Notes' ? styles.notes : undefined}>
                      {col.getValue(club) ?? ''}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </Modal>
  );
}
